import type {
  AtendimentoCliente,
  AtendimentoModo,
  AtendimentoSituacao,
  Consumo,
  OperadorSession,
} from '../types';

// Contexto do atendimento (PRAT-64): diz às telas reaproveitadas (cardápio,
// sacola e checkout) que estão dentro do fluxo de atendimento desta loja.
export const ATENDIMENTO_STORAGE_KEY = 'atendimento';

// JWT do operador (modo assistido). Só no sessionStorage: some ao fechar a aba e
// nunca substitui o cookie `x-auth-token` do cliente.
export const OPERADOR_STORAGE_KEY = 'atendimento-operador';

// Disparado quando uma chamada com o token do operador volta 401.
export const OPERADOR_EXPIRADO_EVENT = 'pratigo:atendimento:operador-expirado';

export type AtendimentoSession = {
  slug: string;
  modo: AtendimentoModo;
  // Cliente escolhido na NOVA VENDA (assistido). null = Consumidor Balcão.
  cliente?: AtendimentoCliente | null;
  // Venda balcão só com o nome (PRAT-113): sem cadastro, não vai para a API.
  nomeBalcao?: string | null;
  // Venda salva aberta para continuar (assistido). Sem ela o PDV é Nova venda.
  edicao?: VendaEmEdicao | null;
};

export type VendaEmEdicao = {
  order_id: string;
  display_id: number;
  consumo: Consumo | null;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function readJson(key: string, storage: StorageLike | null): unknown {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown, storage: StorageLike | null) {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage indisponível (modo privado): o fluxo segue sem persistir.
  }
}

function remove(key: string, storage: StorageLike | null) {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignora
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseCliente(value: unknown): AtendimentoCliente | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }
  return {
    id: value.id,
    name: value.name,
    phone: typeof value.phone === 'string' ? value.phone : null,
  };
}

function parseEdicao(value: unknown): VendaEmEdicao | null {
  if (!isRecord(value) || typeof value.order_id !== 'string' || !value.order_id) return null;
  const consumo = value.consumo === 'viagem' || value.consumo === 'local' ? value.consumo : null;
  return { order_id: value.order_id, display_id: Number(value.display_id) || 0, consumo };
}

export function readAtendimentoSession(
  storage: StorageLike | null = defaultStorage()
): AtendimentoSession | null {
  const data = readJson(ATENDIMENTO_STORAGE_KEY, storage);
  if (!isRecord(data) || typeof data.slug !== 'string' || !data.slug) return null;
  if (data.modo !== 'cliente' && data.modo !== 'assistido') return null;
  return {
    slug: data.slug,
    modo: data.modo,
    cliente: parseCliente(data.cliente),
    edicao: parseEdicao(data.edicao),
  };
}

export function writeAtendimentoSession(
  session: AtendimentoSession,
  storage: StorageLike | null = defaultStorage()
) {
  writeJson(ATENDIMENTO_STORAGE_KEY, session, storage);
}

export function clearAtendimentoSession(storage: StorageLike | null = defaultStorage()) {
  remove(ATENDIMENTO_STORAGE_KEY, storage);
}

export function isAtendimentoAtivo(
  session: AtendimentoSession | null,
  slug: string | null | undefined
): session is AtendimentoSession {
  return !!session && !!slug && session.slug === slug;
}

export function readOperadorSession(
  storage: StorageLike | null = defaultStorage()
): OperadorSession | null {
  const data = readJson(OPERADOR_STORAGE_KEY, storage);
  if (!isRecord(data) || typeof data.token !== 'string' || !data.token) return null;
  if (!isRecord(data.merchant) || typeof data.merchant.slug !== 'string') return null;
  if (!isRecord(data.operador)) return null;
  return data as OperadorSession;
}

export function writeOperadorSession(
  session: OperadorSession,
  storage: StorageLike | null = defaultStorage()
) {
  writeJson(OPERADOR_STORAGE_KEY, session, storage);
}

export function clearOperadorSession(storage: StorageLike | null = defaultStorage()) {
  remove(OPERADOR_STORAGE_KEY, storage);
}

const ATENDIMENTO_URL = /\/merchants\/([^/?#]+)\/atendimento(?:[/?#]|$)/;

// Token do operador para uma requisição: só vai nas rotas de atendimento da
// MESMA loja do operador. Qualquer outra chamada segue com o token do cliente.
export function getOperadorTokenForUrl(
  url: string,
  session: OperadorSession | null = readOperadorSession()
): string | null {
  if (!session) return null;
  const match = url.match(ATENDIMENTO_URL);
  if (!match) return null;
  return decodeURIComponent(match[1]) === session.merchant.slug ? session.token : null;
}

// Rotas do atendimento no app.
export function atendimentoHomePath(slug: string) {
  return `/${slug}/atendimento`;
}

// "Meus pedidos" do cliente e o detalhe de um pedido, dentro do atendimento
// (nunca o `/orders` do web-app).
export function atendimentoMeusPedidosPath(slug: string) {
  return `/${slug}/atendimento/meus-pedidos`;
}

export function atendimentoPedidoPath(slug: string, orderId: string) {
  return `/${slug}/atendimento/pedido/${encodeURIComponent(orderId)}`;
}

export function atendimentoMenuPath(slug: string, modo: AtendimentoModo | null) {
  return modo === 'assistido' ? `/${slug}/atendimento/cardapio` : `/${slug}/atendimento`;
}

// Avisa o gestor (quando o app roda num iframe dele) que uma venda foi salva ou
// finalizada.
export function notificarVendaAoGestor(
  venda: { order_id: string; display_id?: number | string; situacao: AtendimentoSituacao },
  win: Window = window
) {
  if (win.parent === win) return false;
  win.parent.postMessage(
    {
      type: 'pratigo:atendimento:venda',
      order_id: venda.order_id,
      display_id: venda.display_id,
      situacao: venda.situacao,
    },
    '*'
  );
  return true;
}
